/* Login — pantalla de acceso (TASK-005).

USO DE <form> REAL:
  Un form con onSubmit permite que el navegador gestione el autocompletado y
  que Enter funcione sin handlers extra. No se usa fetch para las credenciales
  fuera del contexto de auth: el AuthProvider centraliza la llamada.
*/
import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { PALETTE } from "../lib/palette";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      await login(username.trim(), password);
      // Volver al destino original si el interceptor 401 lo guardo en ?next=
      const next = searchParams.get("next");
      navigate(next && next.startsWith("/") ? next : "/", { replace: true });
    } catch (err: unknown) {
      // El backend responde un mensaje generico a proposito: no revela si el
      // usuario existe. Se muestra tal cual, sin inventar detalle.
      const ax = err as { response?: { status?: number; data?: { detail?: string } }; code?: string };
      const status = ax.response?.status;
      if (status === 429) {
        setError("Demasiados intentos. Espera unos minutos y volvé a probar.");
      } else if (status === 401) {
        setError("Usuario o contraseña incorrectos.");
      } else if (status && status >= 500) {
        // El servidor fallo (no son las credenciales). Un mensaje de
        // "usuario o contraseña" aca haria que el usuario cambie su clave
        // creyendo que la escribio mal, y el problema seguiria igual.
        setError("El servidor tuvo un problema. Esperá unos segundos y reintentá.");
      } else if (ax.code === "ERR_NETWORK") {
        setError("No se pudo contactar el servidor. Revisá tu conexión.");
      } else {
        setError(ax.response?.data?.detail ?? "No se pudo iniciar sesión.");
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm flex flex-col gap-4 p-6 rounded-lg shadow-sm"
        style={{ background: "var(--card, #fff)", border: "1px solid var(--border, #e5e7eb)" }}
      >
        <div className="flex items-center gap-2">
          <img src="/favicon-32x32.png" alt="" width={28} height={28} className="brand-icon" />
          <h1 className="text-lg font-semibold">Restaurant Dashboards</h1>
        </div>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Usuario</span>
          <input
            type="text"
            name="username"
            autoComplete="username"
            autoFocus
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="px-3 py-2 rounded-md border text-sm"
            style={{ borderColor: "var(--border, #d1d5db)" }}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Contraseña</span>
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="px-3 py-2 rounded-md border text-sm"
            style={{ borderColor: "var(--border, #d1d5db)" }}
          />
        </label>

        {error && (
          <p role="alert" className="text-sm" style={{ color: "#b91c1c" }}>
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={enviando || !username || !password}
          className="px-4 py-2 rounded-md text-sm font-medium text-white transition-opacity disabled:opacity-50"
          style={{ background: PALETTE.primaryDark }}
        >
          {enviando ? "Entrando…" : "Entrar"}
        </button>
      </form>
    </div>
  );
}
